import React from "react";
import { Clock, Bell, Calendar, HandCoins, Coins, Users, Rocket, MessageSquare, Download, Search, Wifi, Apple, Play, Layers, MapPin } from "lucide-react";
import { PageHero, Section, Container, Eyebrow, H2, Lead, CTAButton, GlassCard, Orbs } from "../components/PageUI";
import { memberTestimonials } from "../mock";
import SEO from "../components/SEO";

const features = [
  { icon: Clock, title: "Prayer Times", desc: "Accurate daily prayer times for your masjid, with Adhan notification reminders." },
  { icon: Bell, title: "Instant Announcements", desc: "Get notified the moment your masjid posts an update: events, emergencies, reminders." },
  { icon: Calendar, title: "Events & Programs", desc: "Browse and enroll in events, lectures, and programs happening at your masjid." },
  { icon: HandCoins, title: "Easy Donations", desc: "Donate to your masjid or active fundraising campaigns in a few taps, securely." },
  { icon: Coins, title: "Zakat", desc: "Calculate and fulfil your Zakat obligation directly through the app." },
  { icon: Users, title: "Community Feed", desc: "Stay connected with what’s happening across your masjid community." },
  { icon: Layers, title: "Follow Multiple Masjids", desc: "Follow your home masjid plus any others you attend, and get prayer times, announcements, and events from all of them in one app." },
  { icon: MapPin, title: "Find Nearby Masjids", desc: "Discover masjids near you with location-based search, perfect for finding a place to pray while traveling or visiting a new city." },
  { icon: Rocket, title: "Fundraising Campaigns", desc: "Support campaigns that matter to your community and track progress in real time." },
  { icon: MessageSquare, title: "Direct Communication", desc: "Communicate with your imam directly. Direct & instant messaging." },
];

const steps = [
  { icon: Download, title: "Download the App", desc: "Available on iOS and Android. Free to download, free to use." },
  { icon: Search, title: "Find Your Masjid", desc: "Search for your masjid by name or location and connect in seconds." },
  { icon: Wifi, title: "Stay Connected", desc: "Prayer times, events, announcements, and donations, all in one place." },
];

const quotes = memberTestimonials;

export default function MemberApp() {
  return (
    <div>
      <SEO
        title="Masjid Suite Member App | Your Masjid In Your Pocket"
        description="Free iOS and Android app for your community: prayer times, announcements, programs, events, and easy donations, all from your masjid's own branded app."
        path="/member-app"
      />
      <PageHero
        eyebrow="Free for everyone"
        title="Your Masjid,"
        gradient="In Your Pocket."
        lead={<>Prayer times, announcements, events, donations, and your full masjid community, all in one free app. Available on iOS and Android.</>}
        cta={<>
          <CTAButton href="https://apps.apple.com/us/app/masjid-suite/id6742171600" target="_blank" rel="noreferrer"><Apple size={16}/> Download on the App Store</CTAButton>
          <CTAButton href="https://play.google.com/store/apps/details?id=com.kabirumar.app&hl=en_US&pli=1" target="_blank" rel="noreferrer" variant="secondary"><Play size={16}/> Get it on Google Play</CTAButton>
        </>}
      />

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="flex justify-center">
            <img src="/assets/member-app.png" alt="Masjid Suite Member App home screen" className="w-[280px] md:w-[320px] h-auto drop-shadow-2xl" draggable={false} />
          </div>
        </Container>
      </Section>

      <Section id="features" className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="Your Masjid Needs.">One App. Everything</H2>
            <div className="mt-6 flex justify-center"><Lead>Stop missing prayer times, announcements, and events. Masjid Suite connects you to your masjid and community, for free.</Lead></div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f,i)=>{ const Icon=f.icon; return (
              <GlassCard key={i}>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4 shadow-md"><Icon size={22}/></div>
                <h3 className="font-display text-lg text-[#1d1d1f] font-semibold mb-2">{f.title}</h3>
                <p className="text-[13px] text-[#4b5563] leading-relaxed">{f.desc}</p>
              </GlassCard>
            );})}
          </div>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="text-center mb-14"><H2 gradient="in 3 Steps">Get Started</H2>
            <div className="mt-6 flex justify-center"><Lead>Download, connect, and stay engaged with your masjid.</Lead></div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {steps.map((s,i)=>{ const Icon=s.icon; return (
              <GlassCard key={i}>
                <div className="text-[#a8862f] font-display text-4xl font-semibold mb-3">0{i+1}</div>
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4"><Icon size={20}/></div>
                <h3 className="font-display text-xl text-[#1d1d1f] font-semibold mb-2">{s.title}</h3>
                <p className="text-[14px] text-[#4b5563] leading-relaxed">{s.desc}</p>
              </GlassCard>
            );})}
          </div>
        </Container>
      </Section>

      <Section className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14"><H2 gradient="the Community">Loved by</H2></div>
          <div className="grid md:grid-cols-3 gap-5">
            {quotes.map((q,i)=>(
              <GlassCard key={i}>
                <div className="text-[#c9a34a] font-display text-5xl leading-none">“</div>
                <p className="text-[15px] text-[#1d1d1f] leading-relaxed font-display italic">{q.q}</p>
                <div className="mt-5 pt-4 border-t border-[#e6e7eb]">
                  <div className="font-display text-[15px] text-[#0e2408] font-semibold">{q.name}</div>
                  <div className="text-[12px] text-[#6b7280]">{q.org}</div>
                </div>
              </GlassCard>
            ))}
          </div>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="glass-frosted rounded-[40px] p-10 md:p-14 text-center shadow-apple-lg">
            <H2 gradient="Today.">Download Free</H2>
            <div className="mt-6 flex justify-center"><Lead>Join Muslims staying connected with their masjid through Masjid Suite.</Lead></div>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <CTAButton to="/contact">Get Started Free</CTAButton>
              <CTAButton to="/software#apps" variant="secondary">See All Features</CTAButton>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
